import { Button, Typography } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { Box, Stack } from "@mui/system";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { ValuationHubCoreModelImage, ValuationHubCoreModelRealEstateAddress } from "@/api/generatedApi";
import { Edit02Icon, PropertyIcon } from "@/assets";
import { CompletenessIndicator } from "@/components/completenessIndicator";
import { MapComponent } from "@/components/map";
import { PanelHeader } from "@/components/panelHeader";
import { DataGroupType } from "@/components/refineRealEstate/dataGroupsConstants";
import { calculateCompleteness } from "@/helpers/realEstateDataValidationAndCompletenessHelpers";
import {
  formatFloorNumber,
  getHouseOrApartmentTypeTranslation,
  getObjectTypeLabelWIthBuildingType,
} from "@/helpers/realEstateParametersHelpers";
import { colors } from "@/theme/tokens/colors";
import { ObjectTypeEnum, PropertyInformation } from "@/types/refineRealEstate";

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  panelContainer: {
    display: "grid",
    gap: "30px",
    backgroundColor: "white",
    padding: "30px",
    gridTemplateRows: "min-content 1fr auto",
  },
  dataLabel: {
    fontWeight: "400",
    fontSize: "14px",
    lineHeight: "19px",
    fontFamily: "Open Sans",
    color: "#333333",
  },
  panelBodyContainer: {
    display: "grid",
    gap: "30px",
    gridTemplateColumns: {
      xs: "3fr 2fr",
      md: "1fr",
      lg: "1fr",
      xl: "3fr 2fr",
    },
  },
  analysisActionButton: {
    backgroundColor: "background.paper",
  },
  analysisActionBanner: {
    display: "flex",
    gap: "23px",
    flexWrap: "wrap",
    justifyContent: "space-between",
    backgroundColor: theme.semanticColors.surface.blueBg,
    alignItems: "center",
    padding: "20px",
    borderRadius: "4px",
  },
  analysisActionBannerContent: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flex: "1 1 0",
    minWidth: 0,
  },
});

interface RealEstateDataPanelProps {
  panelDescription: string;
  address: ValuationHubCoreModelRealEstateAddress | null;
  propertyInformation: PropertyInformation | null;
  images: ValuationHubCoreModelImage[] | null;
  setRefineData: (selectedDataGroup: DataGroupType | undefined) => void;
  propertyDataIsLoading?: boolean;
  valuationIsLoading?: boolean;
}

export const RealEstateDataPanel = ({
  panelDescription,
  address,
  propertyInformation,
  images,
  setRefineData,
  propertyDataIsLoading,
  valuationIsLoading,
}: RealEstateDataPanelProps): JSX.Element => {
  const { t } = useTranslation("ValuationReport");
  const { t: tRealEstate } = useTranslation("Common", { keyPrefix: "realEstate" });
  const theme = useTheme();

  const styles = createStyles(theme);

  const titleImage = Array.isArray(images)
    ? images.find((image) => image.is_profile_picture)?.url || images[0]?.url || undefined
    : undefined;

  const dataCompleteness = propertyInformation ? calculateCompleteness(propertyInformation) : 0;

  return (
    <Box sx={styles.panelContainer}>
      <PanelHeader
        title={t("propertyData.propertyData")}
        description={panelDescription}
        icon={<PropertyIcon sx={{ fontSize: theme.analysisLayout.headerIconSize, color: colors.brand.brownDark }} />}
      />

      <Box sx={styles.panelBodyContainer}>
        <Box sx={{ display: "grid", gap: "20px", gridTemplateRows: "auto auto 1fr" }}>
          {!propertyDataIsLoading && (
            <Stack spacing={"12px"} alignItems={"flex-start"}>
              <Typography variant="addressLabel" data-cy="real-estate-data-address-row-1">
                {address?.street} {address?.house_number}
              </Typography>
              <Typography data-cy="real-estate-data-address-row-2">
                {address?.post_code} {address?.town}, {address?.nation}
              </Typography>
            </Stack>
          )}
          {!propertyDataIsLoading && (
            <Box sx={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
              {propertyInformation?.buildingData.livingArea && (
                <Stack spacing={"8px"}>
                  <Typography
                    variant="inputLabel"
                    data-cy="real-estate-data-living-area"
                  >{`${propertyInformation?.buildingData.livingArea.toLocaleString("de-DE")} m²`}</Typography>
                  <Typography sx={styles.dataLabel}>{t("propertyData.livingArea")}</Typography>
                </Stack>
              )}
              {propertyInformation?.generalInfo.objectType !== ObjectTypeEnum.house &&
                propertyInformation?.buildingData.rooms !== undefined &&
                propertyInformation?.buildingData.rooms !== null && (
                  <Stack spacing={"8px"}>
                    <Typography
                      variant="inputLabel"
                      data-cy="real-estate-data-rooms"
                    >{`${propertyInformation?.buildingData.rooms} ${t("propertyData.rooms")}`}</Typography>
                    {propertyInformation && (
                      <Typography sx={styles.dataLabel} data-cy="real-estate-data-object-type">
                        {getObjectTypeLabelWIthBuildingType(
                          propertyInformation.generalInfo.objectType,
                          tRealEstate,
                          propertyInformation?.generalInfo.buildingType,
                        )}
                      </Typography>
                    )}
                    {!propertyInformation && <div />}
                  </Stack>
                )}

              {propertyInformation?.generalInfo.objectType === ObjectTypeEnum.house && (
                <Stack spacing={"8px"}>
                  <Typography variant="inputLabel" data-cy="real-estate-data-house-type">
                    {propertyInformation?.buildingData.houseOrApartmentType
                      ? getHouseOrApartmentTypeTranslation(
                          propertyInformation.buildingData.houseOrApartmentType,
                          tRealEstate,
                        )
                      : ""}
                  </Typography>
                  {propertyInformation && (
                    <Typography sx={styles.dataLabel} data-cy="real-estate-data-object-type">
                      {getObjectTypeLabelWIthBuildingType(
                        propertyInformation.generalInfo.objectType,
                        tRealEstate,
                        propertyInformation?.generalInfo.buildingType,
                      )}
                    </Typography>
                  )}
                  {!propertyInformation && <div />}
                </Stack>
              )}

              {propertyInformation?.generalInfo.objectType === ObjectTypeEnum.apartment &&
                propertyInformation?.buildingData.floorNumber !== undefined && (
                  <Stack spacing={"8px"}>
                    <Typography variant="inputLabel" data-cy="real-estate-data-floor-number">
                      {formatFloorNumber(propertyInformation.buildingData.floorNumber, tRealEstate)}
                    </Typography>
                    <Typography sx={styles.dataLabel}>{t("propertyData.floor")}</Typography>
                  </Stack>
                )}
              {propertyInformation?.generalInfo.objectType !== ObjectTypeEnum.apartment &&
                propertyInformation?.plotData.plotArea && (
                  <Stack spacing={"8px"}>
                    <Typography
                      variant="inputLabel"
                      data-cy="real-estate-data-plot-area"
                    >{`${propertyInformation?.plotData.plotArea.toLocaleString("de-DE")} m²`}</Typography>
                    <Typography sx={styles.dataLabel}>{t("propertyData.plotArea")}</Typography>
                  </Stack>
                )}
              {propertyInformation?.buildingData.constructionYear && (
                <Stack spacing={"8px"}>
                  <Typography variant="inputLabel" data-cy="real-estate-data-construction-year">
                    {propertyInformation?.buildingData.constructionYear}
                  </Typography>
                  <Typography sx={styles.dataLabel}>{t("propertyData.constructionYear")}</Typography>
                </Stack>
              )}
            </Box>
          )}
          {propertyDataIsLoading && <Box sx={{ height: "150px" }} />}

          <Box
            sx={{
              display: "grid",
              gap: "20px",
              gridTemplateColumns: {
                xs: "1fr",
                md: "3fr 2fr",
                lg: "3fr 2fr",
                xl: "1fr",
              },
            }}
          >
            <Box sx={{ minHeight: "120px" }}>
              <MapComponent
                markerCoord={address?.coordinates ? [address.coordinates.lng, address.coordinates.lat] : undefined}
              />
            </Box>

            <Box
              sx={{
                position: "relative",
                height: titleImage ? "auto" : "120px",
                maxHeight: "420px",
                display: {
                  xs: "none",
                  md: "block",
                  lg: "block",
                  xl: "none",
                },
              }}
            >
              {titleImage && (
                <img
                  src={titleImage}
                  alt="Property image"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  data-cy="real-estate-data-image"
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            position: "relative",
            height: titleImage ? "auto" : "120px",
            maxHeight: "420px",
            display: {
              xs: "block",
              md: "none",
              lg: "none",
              xl: "block",
            },
          }}
        >
          {titleImage && (
            <img src={titleImage} alt="Property image" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          )}
        </Box>
      </Box>

      <Box sx={styles.analysisActionBanner}>
        <Box sx={styles.analysisActionBannerContent}>
          <CompletenessIndicator realEstateState={propertyInformation || undefined} size={48} />
          <Typography>
            {dataCompleteness === 100 ? t("propertyData.dataComplete") : t("propertyData.optimizeAccuracy")}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          endIcon={<Edit02Icon />}
          onClick={() => {
            setRefineData("generalInfo");
          }}
          disabled={valuationIsLoading}
          sx={styles.analysisActionButton}
        >
          {t("propertyData.refineData")}
        </Button>
      </Box>
    </Box>
  );
};
